/**
 * Group Management Component
 *
 * Manage user groups for better organization
 * Features:
 * - Create/Edit/Delete groups
 * - Assign users to groups
 * - Department-based grouping
 * - View group members
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Badge,
  Avatar,
  List,
  Typography,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  UserOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from 'react-i18next';
import { toast } from "react-toastify";
import { get_departments } from '@/utils/api';
import { accessControlUtils } from '@/components/AccessControlUtils';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const GroupManagement = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [viewMembersModal, setViewMembersModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [form] = Form.useForm();

  const access = accessControlUtils("User Management");

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';

  useEffect(() => {
    loadGroups();
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      await get_departments((res) => {
        if (res.data && res.data.data) {
          setDepartments(res.data.data);
        }
      });
    } catch (error) {
      console.error("Failed to load departments:", error);
    }
  };

  const loadGroups = async () => {
    setLoading(true);
    try {
      // Replace with your actual API call
      // For now using mock data
      const mockGroups = [
        {
          group_id: 1,
          group_name: "Production Team A",
          description: "Morning shift production team",
          department_id: 1,
          department_name: "Production",
          member_count: 12,
          is_active: true,
        },
        {
          group_id: 2,
          group_name: "QC Lab Team",
          description: "Quality control laboratory team",
          department_id: 2,
          department_name: "Quality Control",
          member_count: 8,
          is_active: true,
        },
      ];
      setGroups(mockGroups);
    } catch (error) {
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingGroup(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    form.setFieldsValue({
      group_name: group.group_name,
      description: group.description,
      department_id: group.department_id,
      is_active: group.is_active !== false,
    });
    setModalVisible(true);
  };

  const handleDelete = async (groupId) => {
    try {
      // Replace with your actual delete API
      toast.success("Group deleted successfully");
      loadGroups();
    } catch (error) {
      toast.error("Failed to delete group");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Replace with your actual API call
      const groupData = {
        group_name: values.group_name,
        description: values.description,
        department_id: values.department_id,
        is_active: values.is_active !== false,
        ...(editingGroup && { id: editingGroup.group_id }),
      };

      toast.success(editingGroup ? "Group updated successfully" : "Group created successfully");
      setModalVisible(false);
      form.resetFields();
      loadGroups();
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  const handleViewMembers = (group) => {
    setSelectedGroup(group);
    setViewMembersModal(true);
  };

  const filteredGroups = groups.filter(group =>
    group.group_name?.toLowerCase().includes(searchText.toLowerCase()) ||
    group.description?.toLowerCase().includes(searchText.toLowerCase()) ||
    group.department_name?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: "Group Name",
      dataIndex: "group_name",
      key: "group_name",
      width: "25%",
      render: (text, record) => (
        <Space>
          <TeamOutlined style={{ color: "#52c41a", fontSize: 18 }} />
          <strong>{text}</strong>
          {!record.is_active && <Tag color="red">Inactive</Tag>}
        </Space>
      ),
    },
    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
      width: "20%",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (text) => text || <span style={{ color: "#999" }}>No description</span>,
    },
    {
      title: "Members",
      key: "members",
      width: "10%",
      align: "center",
      render: (_, record) => (
        <Tooltip title="View group members">
          <Badge count={record.member_count || 0} showZero>
            <Button
              type="link"
              icon={<UserOutlined style={{ fontSize: 18 }} />}
              onClick={() => handleViewMembers(record)}
            />
          </Badge>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "15%",
      align: "center",
      render: (_, record) => (
        <Space>
          {access?.update_access && (
            <Tooltip title="Edit Group">
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}
          {access?.delete_access && (
            <Popconfirm
              title="Delete this group?"
              description="Users in this group will be unassigned."
              onConfirm={() => handleDelete(record.group_id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{
                className: "bg-red-600 text-white hover:bg-red-700",
              }}
            >
              <Tooltip title="Delete Group">
                <Button type="link" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Mock members data
  const mockMembers = [
    { id: 1, name: "John Doe", role: "Production Operator", avatar: null },
    { id: 2, name: "Jane Smith", role: "Supervisor", avatar: null },
  ];

  return (
    <>
      <Card
        className="shadow-sm"
        bordered={false}
        style={{ backgroundColor: cardBgColor }}
        title={
          <div className="flex items-center gap-2">
            <Icons.Users className="w-7 h-7 text-blue-600" />
            <Title
              level={4}
              style={{
                color: textColor,
                margin: 0,
                padding: 0,
              }}
            >
              {t('Group Management')}
            </Title>
          </div>
        }
        extra={
          <Space>
            <Input
              placeholder="Search groups..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />
            {access?.create_access && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                style={{
                  backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                  borderColor: isDark ? '#0F172A' : defaultColors.primary,
                }}
              >
                Add Group
              </Button>
            )}
          </Space>
        }
      >
        <Table
          dataSource={filteredGroups}
          columns={columns}
          rowKey="group_id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} groups`,
          }}
          size="middle"
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={
          <Space>
            <TeamOutlined />
            {editingGroup ? "Edit Group" : "Create New Group"}
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={handleSubmit}
        width={600}
        okText={editingGroup ? "Update" : "Create"}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ is_active: true }}
        >
          <Form.Item
            name="group_name"
            label="Group Name"
            rules={[
              { required: true, message: "Please enter group name" },
              { min: 3, message: "Group name must be at least 3 characters" },
            ]}
          >
            <Input placeholder="e.g., Production Team A, QC Lab Team" />
          </Form.Item>

          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: "Please select department" }]}
          >
            <Select placeholder="Select department">
              {departments.map((dept) => (
                <Option key={dept.department_id} value={dept.department_id}>
                  {dept.department_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={3}
              placeholder="Describe the purpose and responsibilities of this group..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Members Modal */}
      <Modal
        title={
          <Space>
            <UserOutlined />
            Group Members - {selectedGroup?.group_name}
          </Space>
        }
        open={viewMembersModal}
        onCancel={() => setViewMembersModal(false)}
        footer={[
          <Button key="close" onClick={() => setViewMembersModal(false)}>
            Close
          </Button>,
        ]}
        width={500}
      >
        <List
          itemLayout="horizontal"
          dataSource={mockMembers}
          renderItem={(member) => (
            <List.Item>
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={member.name}
                description={member.role}
              />
            </List.Item>
          )}
        />
      </Modal>
    </>
  );
};

export default GroupManagement;
